"use client";

import React from "react";

import { Badge } from "@/src/components/shadcn/ui/badge";
import type { ProgressNoteContext } from "@/src/lib/ehr/referral-matching";
import type { ReferralMatchOutput, ReferralMatchReferralSummary } from "@/src/lib/workflows/steps/step-outputs";

interface ReferralMatchPanelProps {
  match: ReferralMatchOutput | undefined;
}

export function ReferralMatchPanelView({ match }: ReferralMatchPanelProps) {
  if (!match) return null;
  if (match.unableToSchedule || match.cannotServicePatient || match.earlySteps) return null;

  if (match.matched && match.matchedReferral) {
    return (
      <div className="flex flex-col gap-2 mt-2">
        <IntakeEvidenceSection evidence={match.intakeEvidence} />
        <p>
          <strong>Matched Referral (verify before completing):</strong>
        </p>
        <ReferralSummaryRow referral={match.matchedReferral} />
      </div>
    );
  }

  if (match.matched) return null;

  const candidates = match.candidateReferrals ?? [];

  if (candidates.length === 0) {
    return (
      <div className="flex flex-col gap-2 mt-2">
        <IntakeEvidenceSection evidence={match.intakeEvidence} />
        <p>
          <strong>Candidate Referrals:</strong>
        </p>
        <p className="text-muted-foreground italic">None in the matching date window.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 mt-2">
      <IntakeEvidenceSection evidence={match.intakeEvidence} />
      <p>
        <strong>Candidate Referrals (verify none match):</strong>
      </p>
      <div className="flex flex-col gap-3">
        {candidates.map((candidate, idx) => (
          <ReferralSummaryRow key={idx} referral={candidate} index={idx + 1} />
        ))}
      </div>
    </div>
  );
}

function IntakeEvidenceSection({ evidence }: { evidence: ProgressNoteContext | undefined }) {
  if (!evidence) return null;

  const providerDisplay =
    [evidence.providerName, evidence.providerNpi ? `NPI: ${evidence.providerNpi}` : null].filter(Boolean).join(" · ") ||
    null;

  const lines: Array<{ label: string; value: string | null }> = [
    {
      label: "Location",
      value: [evidence.clinicName, evidence.clinicAddress].filter(Boolean).join(", ") || null,
    },
    { label: "Provider", value: providerDisplay },
    { label: "Specialty", value: evidence.specialty },
    { label: "Date of event", value: evidence.dateOfEvent },
    { label: "Reason for visit", value: evidence.reasonForVisit },
    {
      label: "ICD-10 codes",
      value: (evidence.diagnosisCodes?.length ?? 0) > 0 ? evidence.diagnosisCodes.join(", ") : null,
    },
  ];

  const visibleLines = lines.filter(({ value }) => value);

  if (visibleLines.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 mb-1">
      <p>
        <strong>Intake:</strong>
      </p>
      <div className="pl-4 border-l-2 border-blue-500/30">
        {visibleLines.map(({ label, value }) => (
          <p key={label} className="whitespace-pre-line">
            <strong>{label}:</strong> {value}
          </p>
        ))}
      </div>
    </div>
  );
}

function ReferralSummaryRow({ referral, index }: { referral: ReferralMatchReferralSummary; index?: number }) {
  const lines: Array<{ label: string; value: string | null }> = [
    { label: "Facility", value: referral.performerName },
    { label: "Facility address", value: referral.performerAddress },
    { label: "Ordering provider", value: referral.requesterName },
    { label: "Specialty", value: referral.specialty },
    { label: "Reason", value: referral.reasonText },
    { label: "ICD codes", value: referral.icdCodes.length > 0 ? referral.icdCodes.join(", ") : null },
    { label: "CPT codes", value: referral.cptCodes.length > 0 ? referral.cptCodes.join(", ") : null },
    {
      label: "Date range",
      value:
        referral.startDate || referral.endDate ? `${referral.startDate ?? "?"} → ${referral.endDate ?? "?"}` : null,
    },
  ];

  return (
    <div className="pl-4 border-l-2 border-green-500/30">
      {index != null && (
        <p className="flex items-center gap-2">
          <strong>#{index}</strong>
          {referral.status && <Badge variant="secondary">{referral.status}</Badge>}
        </p>
      )}
      {index == null && referral.status && (
        <p>
          <Badge variant="secondary">{referral.status}</Badge>
        </p>
      )}
      {lines.map(({ label, value }) =>
        value ? (
          <p key={label} className="whitespace-pre-line">
            <strong>{label}:</strong> {value}
          </p>
        ) : null
      )}
    </div>
  );
}
